import { useState } from 'react'

import { PermissionsGetManagePermissionPermissionsQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Toggler from 'modules/core/components/Toggler'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import permissions from 'modules/permissions/store/collections/permissions'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

type Permission =
  PermissionsGetManagePermissionPermissionsQuery['permissions']['permissions'][number]

interface Props {
  disabled: boolean
  permissionGroup: PermissionGroup
  verb: string
  permission: Permission
  afterToggle: () => Promise<void>
}

function PermissionToggle({ disabled, permissionGroup, verb, permission, afterToggle }: Props) {
  const [saving, setSaving] = useState(false)

  const upsert = async (authorized: boolean) => {
    setSaving(true)
    app.setProgress({
      message: i18n.t('common.loading'),
    })
    // Context is hardcoded to 'everybody'
    // but it really means 'without context'.
    await asyncAction(async (): Promise<void> => {
      await permissions.upsert({
        verb,
        context: 'everybody',
        authorized,
        permissions_group_id: permissionGroup.attributes.id,
        resource: permission.resource,
      })
      track(trackingEvents.permissions.delegatePermissionsSelected, {
        verb,
        authorized,
        permissions_group_id: permissionGroup.attributes.id,
        resource: permission.resource,
      })
      await afterToggle()
      app.setProgress(null)
      setSaving(false)
    })
  }

  const ariaLabelAction = permission.authorized
    ? i18n.t('settings.permissions.group.toggle.disable')
    : i18n.t('settings.permissions.group.toggle.enable')
  const ariaLabel = `${ariaLabelAction} ${i18n.t('settings.permissions.group.permission')} ${
    permission.verbLabel
  }`

  return (
    <Toggler
      ariaLabel={ariaLabel}
      active={permission.authorized}
      disabled={disabled || saving}
      onChange={() => upsert(!permission.authorized)}
    />
  )
}

export default observer(PermissionToggle)
